import React from 'react';
import { MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../context/AppContext';
import { useFloatingChrome } from '../../context/FloatingChromeContext';

/**
 * Persisted floating-menu position. Coordinates are stored as the
 * button's top-left corner (NOT the pointer) in viewport pixels, on
 * top of any `env(safe-area-inset-*)` insets. Storing pre-inset values
 * means the FAB lands in the same visual slot on devices with or
 * without a notch.
 */
type MenuPosition = { x: number; y: number };

/** Stable per-device key — bumping forces a default-position reset. */
export const KEBAB_MENU_POSITION_STORAGE_KEY = 'dg.kebabMenuPosition';
/** Pointer-distance threshold (px) below which a release is treated as a tap. */
export const DRAG_THRESHOLD_PX = 6;
/** Size of the circular FAB. Matches the design-system 44 px touch target. */
const BUTTON_SIZE = 44;
/** Default inset (px) from the viewport edges. Matches `dg-screen-gutter`. */
const EDGE_INSET = 8;

function readPersistedPosition(): MenuPosition | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEBAB_MENU_POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as MenuPosition).x !== 'number' ||
      typeof (parsed as MenuPosition).y !== 'number' ||
      !Number.isFinite((parsed as MenuPosition).x) ||
      !Number.isFinite((parsed as MenuPosition).y)
    ) {
      return null;
    }
    return { x: (parsed as MenuPosition).x, y: (parsed as MenuPosition).y };
  } catch {
    return null;
  }
}

function writePersistedPosition(pos: MenuPosition): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEBAB_MENU_POSITION_STORAGE_KEY, JSON.stringify(pos));
  } catch {
    /* localStorage may be disabled / full — in-memory position is fine */
  }
}

/** Default top-left corner (matches the legacy in-header trigger slot). */
function getDefaultPosition(): MenuPosition {
  return { x: EDGE_INSET, y: EDGE_INSET };
}

/** Clamp inside the current viewport so the FAB always stays fully visible. */
export function clampToViewport(
  pos: MenuPosition,
  vw: number = typeof window !== 'undefined' ? window.innerWidth || 0 : 0,
  vh: number = typeof window !== 'undefined' ? window.innerHeight || 0 : 0
): MenuPosition {
  const safeW = vw || BUTTON_SIZE + EDGE_INSET * 2;
  const safeH = vh || BUTTON_SIZE + EDGE_INSET * 2;
  const maxX = Math.max(EDGE_INSET, safeW - BUTTON_SIZE - EDGE_INSET);
  const maxY = Math.max(EDGE_INSET, safeH - BUTTON_SIZE - EDGE_INSET);
  const x = Number.isFinite(pos.x) ? pos.x : EDGE_INSET;
  const y = Number.isFinite(pos.y) ? pos.y : EDGE_INSET;
  return {
    x: Math.min(Math.max(x, EDGE_INSET), maxX),
    y: Math.min(Math.max(y, EDGE_INSET), maxY),
  };
}

/**
 * Floating, draggable settings menu trigger. Replaces the legacy
 * in-header `ShellMenuButton` and is mounted ONCE at the app shell
 * level (see `App.tsx`) so it persists across route / phase changes
 * and is never duplicated per screen.
 *
 * Behaviour:
 *  - tap (`< {@link DRAG_THRESHOLD_PX}` px of movement) opens the same
 *    mobile shell drawer the old trigger opened (`setMobileShellNavOpen(true)`)
 *  - drag (≥ threshold) repositions and persists to localStorage
 *  - hidden whenever the `FloatingChromeContext` reports
 *    `suppressFloatingMenu === true` (set by the live camera-scan step)
 *  - hidden when `AppContext.showMobileShellNav` is false (which is
 *    the same gate the old trigger used, so behaviour is preserved on
 *    signed-out / class-presenter / grading-only phases)
 *  - keyboard accessible: it is a real `<button>`, so Tab focuses it,
 *    Enter / Space activate it (the browser default click), and the
 *    drag handlers ignore non-pointer activations.
 *
 * Drag uses pointer events with the standard `setPointerCapture` so a
 * single set of handlers covers mouse, touch and pen on every modern
 * browser. We deliberately do NOT use a `setTimeout` "tap vs drag"
 * fix — the distance threshold alone is enough and is correct on
 * platforms where touchmove fires before the user has actually meant
 * to drag (iOS Safari finger-tremor).
 */
export function DraggableSettingsMenu(): React.ReactElement | null {
  const ctx = React.useContext(AppContext);
  const { suppressFloatingMenu } = useFloatingChrome();
  const { t } = useTranslation();

  const [position, setPosition] = React.useState<MenuPosition>(() =>
    clampToViewport(readPersistedPosition() ?? getDefaultPosition())
  );
  const [isDragging, setIsDragging] = React.useState(false);

  // Honour reduced-motion for the snap-back transition only. The drag
  // itself never animates because the position follows the pointer
  // 1:1; this only affects the easing applied between drops / resizes.
  const prefersReducedMotion = React.useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  }, []);

  // Re-hydrate on mount in case tests mutated localStorage AFTER the
  // initial lazy state ran. Also re-clamps so a smaller viewport
  // never traps the FAB off-screen.
  React.useEffect(() => {
    setPosition(clampToViewport(readPersistedPosition() ?? getDefaultPosition()));
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      setPosition((prev) => clampToViewport(prev));
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  /**
   * Transient drag state lives in a ref so re-renders during the
   * gesture don't tear the pointer-move handler. `moved` flips to
   * true once we've crossed the distance threshold.
   */
  const dragStartRef = React.useRef<{
    pointerStartX: number;
    pointerStartY: number;
    elementStartX: number;
    elementStartY: number;
    moved: boolean;
  } | null>(null);

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      // Ignore secondary / middle clicks so context menus & assistive
      // tooling stay reachable.
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* jsdom / some browsers don't implement pointer capture */
      }
      dragStartRef.current = {
        pointerStartX: e.clientX,
        pointerStartY: e.clientY,
        elementStartX: position.x,
        elementStartY: position.y,
        moved: false,
      };
    },
    [position.x, position.y]
  );

  const handlePointerMove = React.useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const start = dragStartRef.current;
    if (!start) return;
    const dx = e.clientX - start.pointerStartX;
    const dy = e.clientY - start.pointerStartY;
    // Defensive: some test environments (jsdom + PointerEvent) deliver
    // `pointermove` without `clientX` / `clientY`, which would turn dx /
    // dy into NaN. NaN < threshold is false, so without this guard the
    // gesture would silently flip into a "drag" and a real tap would
    // stop opening the drawer. Treat non-finite deltas as no movement.
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
    if (!start.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    start.moved = true;
    setIsDragging(true);
    setPosition(
      clampToViewport({
        x: start.elementStartX + dx,
        y: start.elementStartY + dy,
      })
    );
  }, []);

  const handlePointerUp = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const start = dragStartRef.current;
      dragStartRef.current = null;
      const wasDragging = !!start?.moved;
      setIsDragging(false);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (wasDragging) {
        setPosition((prev) => {
          const clamped = clampToViewport(prev);
          writePersistedPosition(clamped);
          return clamped;
        });
        return;
      }
      // No meaningful movement — treat as a tap and open the drawer.
      ctx?.setMobileShellNavOpen?.(true);
    },
    [ctx]
  );

  const handlePointerCancel = React.useCallback(() => {
    dragStartRef.current = null;
    setIsDragging(false);
  }, []);

  /**
   * Keyboard / synthetic-click activation. Browsers fire a `click`
   * event for Enter / Space on focused `<button>` elements; we route
   * those to `setMobileShellNavOpen` here. Pointer-driven clicks are
   * intentionally NOT bridged through this handler — those are
   * resolved by `handlePointerUp` so a drag-then-release never
   * accidentally opens the drawer.
   */
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // `detail === 0` for keyboard-driven clicks (Enter / Space).
      // For mouse / touch we let the pointerup branch handle the open.
      if (e.detail !== 0) return;
      ctx?.setMobileShellNavOpen?.(true);
    },
    [ctx]
  );

  if (suppressFloatingMenu) return null;
  if (!ctx?.showMobileShellNav) return null;

  const label = t('shell.openMenu');
  // We deliberately do NOT animate `top` / `left` between drops: the
  // drag itself follows the pointer 1:1, and a snap animation after
  // resize is a distraction. We still honour `prefers-reduced-motion`
  // to keep the `active:scale-95` press feedback turned off for users
  // who opted out of motion.
  const animateScale = !prefersReducedMotion;

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-grabbed={isDragging}
      data-testid="draggable-settings-menu"
      data-dragging={isDragging ? 'true' : 'false'}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClick={handleClick}
      style={{
        position: 'fixed',
        top: `calc(${position.y}px + env(safe-area-inset-top, 0px))`,
        left: `calc(${position.x}px + env(safe-area-inset-left, 0px))`,
        touchAction: 'none',
        pointerEvents: 'auto',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      className={
        'lg:hidden z-[90] inline-flex h-11 min-h-[44px] w-11 min-w-[44px] items-center justify-center rounded-full border border-dg-border bg-dg-bg-surface/95 text-dg-fg-primary shadow-dg-card transition-colors hover:bg-dg-interaction-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dg-primary-500 dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-100 dark:hover:bg-slate-800' +
        (animateScale ? ' active:scale-95' : '')
      }
    >
      <MoreVertical className="h-5 w-5" aria-hidden />
    </button>
  );
}
